<template>
    <!-- The thread. `log` and `polite` so that a new answer is read out when it lands
         and the whole conversation is not re-read every time — `additions` is the half
         that makes the difference. -->
    <div
        ref="log"
        class="milo-log"
        role="log"
        aria-live="polite"
        aria-relevant="additions"
        :aria-busy="chatStore.sending"
    >
        <div v-if="!chatStore.messages.length && !chatStore.loading" class="milo-empty">
            <h3>{{ $t('milo.greeting') }}</h3>
            <p>{{ $t('milo.intro') }}</p>
        </div>

        <template v-for="(bubble, index) in bubbles" :key="index">
            <!-- What the guest wrote is drawn as it was typed. What MILO wrote is split
                 first, so that the map address under "how do I get to the castle?" is
                 something a thumb can follow — see libs/linkify.ts. Both halves are
                 interpolated and neither goes anywhere near v-html. -->
            <p v-if="!bubble.segments" class="milo-bubble me">{{ bubble.text }}</p>

            <p v-else class="milo-bubble milo">
                <template v-for="(segment, part) in bubble.segments" :key="part">
                    <a
                        v-if="segment.kind === 'link'"
                        class="milo-link"
                        :href="segment.url"
                        :title="segment.url"
                        target="_blank"
                        rel="noopener"
                    >{{ segment.text }}</a>

                    <template v-else>{{ segment.text }}</template>
                </template>
            </p>

            <MiloOperationCard
                v-for="operation in (bubble.operations ?? [])"
                :key="operation.id"
                :operation="operation"
                :note="chatStore.noteFor(operation.id)"
                :deciding="chatStore.deciding === operation.id"
                @decide="confirm => decide(operation.id, confirm)"
            />
        </template>

        <p v-if="chatStore.sending" class="milo-thinking">
            {{ $t('milo.thinking') }}
            <small v-if="chatStore.stillWorking">{{ $t('milo.still_working') }}</small>
        </p>
    </div>

    <!-- The four openings, and only while the thread is empty: a chip beside an answer
         is a suggestion to start again. They send a whole SENTENCE and not their own
         label, so what the model is asked is a question and not a key. -->
    <div v-if="!chatStore.messages.length" class="milo-chips">
        <button
            v-for="chip in chips"
            :key="chip.label"
            type="button"
            class="milo-chip"
            :disabled="chatStore.sending"
            @click="ask(chip.text)"
        >{{ chip.label }}</button>
    </div>

    <p v-if="chatStore.notice" class="milo-note">{{ $t(chatStore.notice) }}</p>

    <!--
        The question box. NOT a native <form> and not UForm either: there is no Nuxt UI
        in this portal, and Enter is bound here rather than borrowed from a form element
        — which is the one behaviour a <form> would have been for. Shift+Enter still
        makes a new line, because a guest writing a message to the desk writes more than
        one.
    -->
    <div class="milo-ask">
        <textarea
            ref="box"
            v-model="input"
            rows="1"
            :placeholder="$t('milo.placeholder')"
            :maxlength="MAX_LENGTH"
            :aria-label="$t('milo.placeholder')"
            :disabled="chatStore.sending"
            @input="grow"
            @keydown.enter.exact.prevent="ask()"
        ></textarea>

        <button
            class="milo-send"
            type="button"
            :aria-label="$t('milo.send')"
            :disabled="chatStore.sending || !input.trim()"
            @click="ask()"
        >➤</button>
    </div>

    <p class="milo-disclaimer">{{ $t('milo.disclaimer') }}</p>
</template>

<script setup lang="ts">
    import { computed, nextTick, ref, watch } from 'vue'
    import { useI18n } from 'vue-i18n'

    import MiloOperationCard from '@/components/MiloOperationCard.vue'
    import { linkify } from '@/libs/linkify'
    import { useChatStore } from '@/stores/chat'

    /**
     * The conversation itself: the bubbles, the cards, the openings and the box.
     *
     * Its own component and not part of the sheet, for the reason the reception portal
     * split the same thing out: the sheet is a FRAME — a grip, a title, a way to shut
     * it — and the day this portal grows a page of its own for Milo it draws exactly
     * this and nothing is copied.
     */

    /** What one question may be, matching `MaxGuestChatMessageLength` on the service.
     *  Two thousand and not the desk's eight: a guest types with a thumb, and the
     *  reception's limit is sized for a receptionist pasting an e-mail in. */
    const MAX_LENGTH = 2000

    const { t } = useI18n()

    const chatStore = useChatStore()

    const log   = ref<HTMLElement | null>(null)
    const box   = ref<HTMLTextAreaElement | null>(null)
    const input = ref('')

    /**
     * The four openings.
     *
     * From i18n and not from code, and the late checkout drops out when the house has
     * that action switched off: a button that leads to a refusal is worse than a button
     * that is not there. The other three ask questions Milo answers from the house's own
     * cards, which every house has.
     *
     * The keys are written out one by one rather than assembled from a list of names,
     * for the reason CLAUDE.md deviation 10 gives: a key built by concatenation is
     * invisible to `yarn check-i18n`, and a chip whose text nobody translated would send
     * the model the string "milo.chip_wifi_text" as a question.
     */
    const chips = computed(() => [
        { label: t('milo.chip_wifi'),      text: t('milo.chip_wifi_text') },
        { label: t('milo.chip_breakfast'), text: t('milo.chip_breakfast_text') },
        ...(chatStore.actions.late_checkout
            ? [{ label: t('milo.chip_late_checkout'), text: t('milo.chip_late_checkout_text') }]
            : []),
        { label: t('milo.chip_today'),     text: t('milo.chip_today_text') },
    ])

    /**
     * Markdown emphasis and headings taken back out of MILO's own lines.
     *
     * The bubbles are plain text — interpolated, never `v-html` — so nothing renders
     * Markdown here: a model that answers "**Slovenska cesta 2**" puts the four
     * asterisks on the guest's screen, and a line beginning "## Naslov" shows the
     * hashes. The prompt now forbids both (docs/AI.md §24), and this is the second
     * lock on the same door: a model that slips is a guest reading punctuation,
     * standing on a pavement.
     *
     * It takes the MARKERS out and never the text between them, and it leaves
     * `[label](https://…)` alone — that is the one shape the prompt asks for, and
     * splitting it is `libs/linkify.ts`'s business.
     */
    const withoutMarkdown = (text: string) => text
        .replace(/^ {0,3}#{1,6} +/gm, '')
        .replace(/\*\*|__/g, '')

    /**
     * The thread, with Milo's own lines already split into segments.
     *
     * A computed and not a call in the template: `linkify` would otherwise run over
     * every line of the conversation on every render — including the ones caused by
     * the box growing as somebody types. `segments` is null for the guest's own line,
     * which is what the template reads to decide which of the two bubbles to draw:
     * a link the GUEST typed stays text, because nothing in this portal should teach a
     * guest that an address pasted into a chat becomes something to tap.
     */
    const bubbles = computed(() => chatStore.messages.map(message => ({
        text:       message.text,
        operations: message.operations,
        segments:   message.role === 'user' ? null : linkify(withoutMarkdown(message.text)),
    })))

    const scrollDown = async () => {
        await nextTick()
        log.value?.scrollTo({ top: log.value.scrollHeight })
    }

    /** The box grows with what is typed and stops where the CSS says. */
    const grow = () => {
        const element = box.value

        if (!element) return

        element.style.height = 'auto'
        element.style.height = `${element.scrollHeight}px`
    }

    const ask = async (preset?: string) => {
        const text = (preset ?? input.value).trim()

        if (!text || chatStore.sending) return

        input.value = ''
        await nextTick()
        grow()

        await chatStore.send(text)
    }

    /** Confirming or declining a card is the store's business; this only names which
     *  card, and the two verbs are the store's own so the sheet cannot invent a third. */
    const decide = (operationId: string, confirm: boolean) =>
        (confirm ? chatStore.confirm(operationId) : chatStore.decline(operationId))

    watch(() => chatStore.messages.length, scrollDown)
    watch(() => chatStore.sending, scrollDown)

    /** The sheet puts the cursor here when it opens. */
    const focus = () => box.value?.focus()

    defineExpose({ focus, scrollDown })
</script>
